import clsx from "clsx";
import React, { useRef, useState } from "react";
import {
  Card,
  CardProps,
  Box,
  Typography,
  Button,
  Portal,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowForwardRounded } from "@mui/icons-material";
import omit from "lodash.omit";
import {
  usePopupState,
  bindToggle,
  bindPopper,
} from "material-ui-popup-state/hooks";

import { colorSystem } from "../../theme";
import { ClientGoalsDrawer } from "../client-goals/ClientGoalsDrawer";
import { ClientGoal } from "../client-goals/ClientGoal";
import ClientGoalsClient from "../../services/ClientGoalsClientService";
import { ReactComponent as GoalsIcon } from "../../icons/Goals.svg";

import { useQuery } from "@tanstack/react-query";
import { ClientGoalsCardSkeleton } from "../loading/ClientGoalsCardSkeleton";
import { JourneyAnchors } from "../../constants";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import { ProfileIconBlock } from "../coach-clients/ProfileIconBlock";
import { useContainerQuery } from "../../hooks/useContainerQuery";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    containerType: "inline-size",
    position: "relative",
  },
  topRight: {
    position: "absolute",
    right: 0,
  },
  setButton: {
    backgroundColor: colorSystem.gray1,
    color: theme.palette.common.white,
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    height: 44,
    padding: theme.spacing(1.5, 7.5),
    [theme.containerQueries.down("sm")]: {
      marginTop: theme.spacing(2),
    },
  },

  updateButton: {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "none",
    height: theme.spacing(4),
    [theme.containerQueries.down("sm")]: {
      marginTop: theme.spacing(2),
    },
  },

  updateButtonIcon: {
    color: theme.palette.primary.main,
    fontSize: "25px!important",
  },
  arrowIconDown: {
    transform: "rotate(180deg)",
  },
  goals: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    marginTop: theme.spacing(4.5),

    [theme.containerQueries.up("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },

    [theme.containerQueries.up("md")]: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },

  goal: {
    justifyContent: "space-between",
    marginRight: theme.spacing(3),

    [theme.containerQueries.down("sm")]: {
      marginRight: theme.spacing(0),
    },

    "&:nth-child(n+2)": {
      marginTop: theme.spacing(3),
    },

    [theme.containerQueries.up("sm")]: {
      "&:nth-child(n+2)": {
        marginTop: "initial",
      },
      "&:nth-child(n+3)": {
        marginTop: theme.spacing(3),
      },
    },

    [theme.containerQueries.up("md")]: {
      "&:nth-child(n+3)": {
        marginTop: "initial",
      },
      "&:nth-child(n+4)": {
        marginTop: theme.spacing(3),
      },
    },
  },

  goalsTopContainer: {
    display: "flex",
    alignItems: "center",
  },

  goalsContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    marginLeft: theme.spacing(2),
  },

  empty: {
    "&:not(:last-child)": {
      paddingRight: theme.spacing(1),
    },
  },
}));

export interface ClientGoalsCardProps extends CardProps {
  clientId: number;
  isPreview?: boolean;
  isFirst?: boolean;
  collapsable?: boolean;
  defaultOpen?: boolean;
}

export const CLIENT_GOALS_LIST_QUERY_KEY = "client-goals-list";

export function ClientGoalsCard(props: ClientGoalsCardProps) {
  const {
    className,
    clientId,
    isPreview,
    isFirst,
    collapsable,
    defaultOpen,
    ...other
  } = props;

  const s = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);

  const smUp = useContainerQuery(containerRef, (theme) =>
    theme.containerQueries.up("sm"),
  );
  const smOnly = useContainerQuery(containerRef, (theme) =>
    theme.containerQueries.only("sm"),
  );

  const itemPerRow = smOnly ? 2 : 3;
  const cardPageSize = itemPerRow * 3;

  const [isOpenContent, setIsOpenContent] = React.useState(!!defaultOpen);

  const drawerState = usePopupState({
    variant: "popover",
    popupId: "update-goals",
  });

  const { data: goalsData, isLoading } = useQuery({
    queryKey: [CLIENT_GOALS_LIST_QUERY_KEY, { clientId }],
    queryFn: () => ClientGoalsClient.getAllClientGoals(clientId),
  });
  const goals = goalsData?.items;
  const cardClientGoals = goals?.slice(0, cardPageSize);
  const latestModifiedGoal = [...(goals ?? [])]
    .filter((goal) => goal.lastModified)
    .sort((a, b) => b.lastModified.valueOf() - a.lastModified.valueOf())[0];

  return (
    <>
      <Card
        ref={containerRef}
        className={clsx(s.root, className, JourneyAnchors.CLIENT_GOALS)}
        elevation={0}
        variant="outlined"
        {...other}
      >
        <ClientDetailsCardHeader
          icon={<ProfileIconBlock icon={<GoalsIcon />} bgVariant="accent" />}
          title="Goals"
          subtitle={
            cardClientGoals?.length &&
            latestModifiedGoal && (
              <>
                Last recorded{" "}
                {latestModifiedGoal.lastModified.format("MMM DD, YYYY")}
              </>
            )
          }
          showSubtitleSkeleton={isLoading}
          isOpenContent={isOpenContent}
          setIsOpenContent={setIsOpenContent}
          isFirst={isFirst}
          collapsable={collapsable}
          defaultOpen={defaultOpen}
        >
          {!isLoading && (
            <>
              {goals?.length && smUp ? (
                <Button
                  className={clsx(s.updateButton, s.topRight)}
                  endIcon={
                    <ArrowForwardRounded className={s.updateButtonIcon} />
                  }
                  children="See more"
                  {...bindToggle(drawerState)}
                />
              ) : (
                smUp && (
                  <Button
                    className={clsx(s.setButton, s.topRight)}
                    variant="contained"
                    children="Set Goals"
                    {...bindToggle(drawerState)}
                  />
                )
              )}
            </>
          )}
        </ClientDetailsCardHeader>

        {isOpenContent && (
          <>
            {isLoading ? (
              <ClientGoalsCardSkeleton />
            ) : (
              <>
                <Box className={s.goals}>
                  {cardClientGoals?.length ? (
                    cardClientGoals.map((goal) => (
                      <ClientGoal
                        key={goal.id}
                        className={s.goal}
                        goal={goal}
                        clientId={clientId}
                      />
                    ))
                  ) : (
                    <Typography
                      sx={{ color: (theme) => theme.palette.text.secondary }}
                    >
                      No goals to show. You can set new goals.
                    </Typography>
                  )}
                </Box>
                {goals?.length && !smUp ? (
                  <Button
                    className={s.updateButton}
                    endIcon={
                      <ArrowForwardRounded className={s.updateButtonIcon} />
                    }
                    children="See more"
                    {...bindToggle(drawerState)}
                  />
                ) : (
                  !smUp && (
                    <Button
                      className={s.setButton}
                      variant="contained"
                      children="Set Goals"
                      {...bindToggle(drawerState)}
                    />
                  )
                )}
              </>
            )}
          </>
        )}
      </Card>

      {drawerState.isOpen && (
        <Portal>
          <ClientGoalsDrawer
            {...omit(bindPopper(drawerState), ["anchorEl"])}
            onClose={drawerState.close}
            goalsData={goalsData}
            clientId={clientId}
          />
        </Portal>
      )}
    </>
  );
}
